import { Box } from '@material-ui/core'
import { ArticleAttributes } from 'components/facets/ArticleAttributes'
import { RarityBadge } from 'components/facets/RarityBadge'
import { useUser } from 'components/providers/UserProvider'
import { getPriceFormat } from 'lib/utils'
import {
  ARTICLE_STATES,
  getDisplayLocationName,
  getPlaysetCoefficient,
  Location,
  OrderArticle,
} from 'shared'
import { ESCAPE_STRING_FOR_EMPTY_COMMENT } from 'utils/constants'
import { getPickedQuantityLabel, locationBeforePick } from './PickingPresenter'

export interface CardInfoBoxProps {
  article: OrderArticle
  type?: string
  horizontalView?: boolean
}

export const CardInfoBox = ({ article, type, horizontalView }: CardInfoBoxProps): JSX.Element => {
  const { user } = useUser()
  const showAvailableQuantity = user.showAvailableQuantityInPickingLocations ?? false

  const listOfChosenLocations = (location: Location | null, article: OrderArticle) => {
    if (!location) {
      return (
        <span
          className="picking-location-name"
          data-testid={'missing-location-' + article.ptArticleId}
          title="Missing location. Card not found in inventory"
        >
          Missing location. Card not found in inventory
        </span>
      )
    } else {
      if (article.articleState === ARTICLE_STATES.PICKED) {
        return (
          <div key={location.name} className="d-flex flex-row">
            <span
              className="text-wrap picking-location-name lh-1 "
              title={getDisplayLocationName(location.name)}
            >
              {getDisplayLocationName(location.name)}:
            </span>
            <span className="me-1 flex-shrink-0">{getPickedQuantityLabel(article)}</span>
          </div>
        )
      } else {
        return (
          <span
            className="picking-location-name lh-1 "
            title={getDisplayLocationName(location.name)}
          >
            {getDisplayLocationName(location.name)}
          </span>
        )
      }
    }
  }

  const articleComment = () => {
    if (article.comment === ESCAPE_STRING_FOR_EMPTY_COMMENT) return undefined
    return article.comment
  }

  const maxPickingQuantity = article.isPlayset
    ? article.quantity * getPlaysetCoefficient(article.card.idGame!)
    : article.quantity

  return (
    <>
      <div className="d-flex flex-column">
        {/* price and pickignId */}
        <div className="d-flex flex-row justify-content-between">
          <span className="fs-6">{getPriceFormat(article.price, article.currencyCode)}</span>
          <span className="fs-6"> {article.pickingId}</span>
          <span className="fw-medium fs-6">
            Picked:
            {article.pickedQuantity}/{maxPickingQuantity}
          </span>
        </div>
        {!horizontalView && (
          <div className="d-flex flex-row my-1">
            {' '}
            <Box width={32}>
              <RarityBadge
                rarity={article.card.rarityStr ?? ''}
                idGame={article.card.idGame!}
                className="me-1"
              />
            </Box>
            {article.card.rarityStr}
          </div>
        )}

        {/* quantity, attributes */}
        <div className="d-flex justify-content-between flex-row align-items-center">
          <div className="d-flex flex-row align-items-center w-100">
            <ArticleAttributes article={article} iconHeight={17} />
          </div>
        </div>

        {/* location */}
        <div className="d-flex mt-1 align-items-center picking-card-location lh-1 ">
          <i
            className="fa-light fa-location-dot me-1 flex-shrink-0"
            style={{ fontSize: '18px' }}
          ></i>
          {type === 'toPick'
            ? locationBeforePick(article, showAvailableQuantity)
            : listOfChosenLocations(article.locationInfo, article)}
        </div>

        {/* comment  */}
        <div className="mt-1 d-flex flex-row align-items-center comment-width text-start lh-1 picking-card-comment">
          <i
            className={`fa-light fa-message-dots comment-icon me-1 mt-1 flex-shrink-0 ${
              articleComment() === undefined || articleComment() === '' ? 'missing-comment' : ''
            }`}
            style={{ fontSize: '18px' }}
          ></i>
          <span
            className="picking-comment-clamp"
            data-testid="article-comment"
            title={articleComment()}
          >
            {articleComment()}
          </span>
        </div>
      </div>
    </>
  )
}
